import { useState } from 'react';
import {
  Monitor,
  Award,
  Calendar,
  CheckCircle2,
  Sparkles,
  Users,
  Settings,
  TrendingUp,
} from 'lucide-react';
import { BRAND, SUBMARCAS } from '@/constants/brand';
import { CATALOGO, ESQUEMAS_PAGO } from '@/constants/catalogo';
import { fmt } from '@/lib/utils';
import XperienceLogo from '@/components/ui/XperienceLogo';
import { ValueCard, PhaseCard } from './Cards';
import ProductoCard from './ProductoCard';

/**
 * Renderiza la propuesta. Se usa en:
 * - QuoteView (vista interna del equipo)
 * - PublicProposal (vista pública del cliente vía /p/:token)
 *
 * El prop `onAccept` solo se pasa cuando el cliente puede aceptar
 * (vista pública con token válido). En vista interna es null.
 */
export default function ProposalRenderer({ quote, onAccept, allowAccept = false }) {
  const [aceptado, setAceptado] = useState(quote.estatus === 'aceptada');
  const [nombreAcepta, setNombreAcepta] = useState('');
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const submarca = SUBMARCAS[quote.submarca || 'group'];

  const handleAccept = async () => {
    if (!nombreAcepta.trim()) return;
    await onAccept({
      nombre: nombreAcepta,
      fecha: new Date().toISOString(),
    });
    setAceptado(true);
    setShowAcceptModal(false);
  };

  const productosOrdenados = [...quote.productos].sort(
    (a, b) => (CATALOGO[a.id]?.orden || 99) - (CATALOGO[b.id]?.orden || 99)
  );

  return (
    <div className="min-h-screen bg-neutral-950 text-white">
      {/* Hero */}
      <div className="relative overflow-hidden bg-black">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `repeating-linear-gradient(45deg, ${BRAND.gold} 0, ${BRAND.gold} 1px, transparent 1px, transparent 40px)`,
          }}
        />
        <div
          className="absolute top-0 right-0 w-96 h-96 rounded-full blur-3xl opacity-10"
          style={{ background: BRAND.gold }}
        />

        <div className="relative max-w-5xl mx-auto px-8 py-20">
          <div className="mb-8 flex items-center gap-4">
            <XperienceLogo className="w-14 h-14" /> 
            <div>
{/*               <div className="text-white font-bold tracking-[0.2em] text-sm">
                {submarca.name.toUpperCase()}
              </div> */}
{/*               <div className="text-[10px] tracking-[0.3em] text-neutral-500">
                {submarca.tag.toUpperCase()}
              </div> */}
            </div>
          </div>

          <div className="text-xs tracking-[0.3em] text-brand-gold mb-4">
            PROPUESTA DE VALOR
          </div>
          <h1 className="text-5xl font-bold mb-4 leading-tight">
            {quote.proyecto.nombre || 'Proyecto'}
          </h1>
          <div className="flex items-center gap-6 text-neutral-400 text-sm">
            <div>{quote.cliente.empresa}</div>
            <div className="w-px h-4 bg-neutral-700" />
            <div>
              {new Date(quote.fechaCreacion).toLocaleDateString('es-MX', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </div>
            <div className="w-px h-4 bg-neutral-700" />
            <div className="font-mono">{quote.folio}</div>
          </div>

          <div className="mt-12 p-6 bg-white/5 backdrop-blur border border-white/10 rounded-xl">
            <div className="text-xs text-neutral-400 mb-2">PARA</div>
            <div className="text-lg text-white font-medium">{quote.cliente.nombre}</div>
            <div className="text-sm text-neutral-400">
              {quote.cliente.puesto} · {quote.cliente.empresa}
            </div>
          </div>
        </div>
      </div>

      {/* Cuerpo */}
      <div className="max-w-5xl mx-auto px-8 py-16">
        {/* Propuesta de valor */}
        <div className="mb-16">
          <div className="text-xs tracking-[0.3em] text-brand-gold mb-4">
            ¿POR QUÉ XPERIENCE?
          </div>
          <h2 className="text-3xl font-bold text-white mb-6">
            One Stop Shop creativo y digital
          </h2>
          <p className="text-neutral-400 text-lg leading-relaxed max-w-3xl">
            Somos el ente creativo que mediante la tecnología, el marketing y la creación
            multimedia busca exponenciar marcas dando nuevas perspectivas que crean nuevas
            oportunidades para los proyectos de nuestros clientes.
          </p>

          <div className="grid grid-cols-4 gap-6 mt-10">
            <ValueCard
              icon={Sparkles}
              title="Innovación"
              desc="Rompemos estándares constantemente"
            />
            <ValueCard
              icon={Users}
              title="Complicidad"
              desc="Aliados auténticos y transparentes"
            />
            <ValueCard
              icon={Settings}
              title="A medida"
              desc="Soluciones sin rodeos"
            />
            <ValueCard
              icon={TrendingUp}
              title="Resultados"
              desc="Impecable y puntual"
            />
          </div>
        </div>

        {/* Click & Xperience deep dive */}
        {quote.productos.some((p) => p.id === 'clickXperience') && (
          <div className="mb-16 p-8 bg-gradient-to-br from-brand-gold/10 to-transparent border border-brand-gold/20 rounded-2xl">
            <div className="flex items-start gap-4 mb-6">
              <Monitor className="w-8 h-8 text-brand-gold mt-1" />
              <div>
                <div className="text-xs tracking-[0.3em] text-brand-gold mb-1">
                  PRODUCTO ESTRELLA
                </div>
                <h3 className="text-2xl font-bold text-white">Click & Xperience</h3>
              </div>
            </div>
            <p className="text-neutral-300 leading-relaxed mb-6">
              Plataforma web interactiva que combina modelado 3D en Unreal Engine,
              herramientas inmersivas de visualización, y sistema inteligente de gestión de
              disponibilidades. Diseñada para que tu equipo comercial cierre ventas con
              mayor contundencia y tus prospectos vivan el proyecto antes de conocerlo
              físicamente.
            </p>
            <div className="grid grid-cols-3 gap-4">
              <PhaseCard
                num="1"
                title="Modelado 3D"
                desc="Masterplan, tipologías y amenidades desde planos"
              />
              <PhaseCard
                num="2"
                title="Entregables"
                desc="Renders, recorridos 360, videos y fotogalería"
              />
              <PhaseCard
                num="3"
                title="Programación"
                desc="Plataforma interactiva con gestión de disponibilidades"
              />
            </div>
            <div className="mt-6 p-4 bg-white/5 rounded-lg flex items-center gap-3">
              <Award className="w-5 h-5 text-brand-gold" />
              <div className="text-sm text-neutral-300">
                Incluye{' '}
                <span className="text-white font-medium">
                  implementación, capacitación y licencia de plataforma por 3 años
                </span>
                . A partir del año 4: renovación anual $33,000 MXN.
              </div>
            </div>
          </div>
        )}

        {/* Alcance */}
        <div className="mb-16">
          <div className="text-xs tracking-[0.3em] text-brand-gold mb-4">ALCANCE</div>
          <h2 className="text-3xl font-bold text-white mb-8">Qué incluye tu propuesta</h2>
          <div className="space-y-4">
            {productosOrdenados.map((p) => (
              <ProductoCard key={p.id} producto={p} />
            ))}
          </div>
        </div>

        {/* Proceso */}
        <div className="mb-16">
          <div className="text-xs tracking-[0.3em] text-brand-gold mb-4">CÓMO TRABAJAMOS</div>
          <h2 className="text-3xl font-bold text-white mb-8">Nuestro proceso</h2>
          <div className="grid grid-cols-5 gap-4">
            {[
              { n: '01', t: 'Acuerdo', d: 'Contrato y planeación' },
              { n: '02', t: 'Kickoff', d: 'Documentos y lineamientos' },
              { n: '03', t: 'Modelado', d: '3D y renders base' },
              { n: '04', t: 'Producción', d: 'Audiovisuales' },
              { n: '05', t: 'Programación', d: 'Plataforma + entrega' },
            ].map((p) => (
              <div
                key={p.n}
                className="p-5 bg-neutral-900/50 border border-neutral-800 rounded-xl"
              >
                <div className="text-3xl font-bold mb-2 text-brand-gold">{p.n}</div>
                <div className="text-white font-medium mb-1">{p.t}</div>
                <div className="text-xs text-neutral-500">{p.d}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Inversión */}
        <div className="mb-16" id="inversion">
          <div className="text-xs tracking-[0.3em] text-brand-gold mb-4">INVERSIÓN</div>
          <h2 className="text-3xl font-bold text-white mb-8">Resumen financiero</h2>

          <div className="bg-neutral-900/50 border border-neutral-800 rounded-2xl overflow-hidden">
            <div className="p-6 space-y-3">
              {productosOrdenados.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center justify-between py-2 border-b border-neutral-800 last:border-0"
                >
                  <div>
                    <div className="text-white font-medium">
                      {CATALOGO[p.id]?.nombre || p.nombre}
                    </div>
                    {p.id === 'clickXperience' && p.config?.proyectos && (
                      <div className="text-xs text-neutral-500 mt-1">
                        {p.config.proyectos.length}{' '}
                        {p.config.proyectos.length === 1 ? 'proyecto' : 'proyectos'} ·{' '}
                        {p.config.variante === 'preventa' ? 'Preventa' : 'Terminado'}
                      </div>
                    )}
                  </div>
                  <div className="text-white font-medium">{fmt(p.precioFinal)}</div>
                </div>
              ))}
              {quote.pago.licenciaPrepagada && (
                <div className="flex items-center justify-between py-2 border-b border-neutral-800">
                  <div>
                    <div className="text-white font-medium">
                      Licencia de plataforma (años 1-3)
                    </div>
                    <div className="text-xs text-neutral-500">
                      Prepago de hosting, mantenimiento y actualizaciones
                    </div>
                  </div>
                  <div className="text-white font-medium">
                    {fmt(quote.pago.licenciaPrepagadaPrecio)}
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 bg-black/30 space-y-2">
              <div className="flex justify-between text-neutral-400 text-sm">
                <span>Subtotal</span>
                <span>{fmt(quote.subtotal)}</span>
              </div>
              {quote.descuentoPct > 0 && (
                <div className="flex justify-between text-green-400 text-sm">
                  <span>Descuento ({quote.descuentoPct}%)</span>
                  <span>-{fmt(quote.descuentoTotal)}</span>
                </div>
              )}
              {quote.ajusteManual !== 0 && (
                <div className="flex justify-between text-neutral-400 text-sm">
                  <span>Ajuste</span>
                  <span>
                    {quote.ajusteManual > 0 ? '+' : ''}
                    {fmt(quote.ajusteManual)}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-white text-2xl font-bold pt-3 border-t border-neutral-800">
                <span>Total</span>
                <span>{fmt(quote.total)}</span>
              </div>
              <div className="text-right text-xs text-neutral-500">+ IVA</div>
            </div>
          </div>

          {/* Esquema de pago */}
          <div className="mt-6 p-5 bg-neutral-900/50 border border-neutral-800 rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <Calendar className="w-4 h-4 text-brand-gold" />
              <div className="text-sm text-white font-medium">Esquema de pago</div>
            </div>
            <div className="text-sm text-neutral-300">
              {ESQUEMAS_PAGO.find((e) => e.id === quote.pago.esquema)?.label}
            </div>
            {quote.pago.notas && (
              <div className="text-xs text-neutral-500 mt-2">{quote.pago.notas}</div>
            )}
          </div>
        </div>

        {/* T&C */}
        <div className="mb-16 p-6 bg-neutral-900/50 border border-neutral-800 rounded-xl">
          <div className="text-xs tracking-[0.3em] text-brand-gold mb-3">
            TÉRMINOS Y CONDICIONES
          </div>
          <div className="space-y-2 text-sm text-neutral-400">
            <p>• Los precios indicados no incluyen IVA.</p>
            <p>
              • Esta propuesta tiene una vigencia de 30 días naturales desde la fecha de
              emisión.
            </p>
            <p>
              • Los tiempos de entrega se definirán en el Plan de Trabajo al inicio del
              proyecto.
            </p>
            <p>
              • Se permiten cambios dentro de las rondas establecidas: 1 ronda en
              seguimientos, 2 rondas en etapas de ajustes.
            </p>
            <p>
              • La propiedad intelectual de las Imágenes del Proyecto será del Cliente;
              Xperience conserva derechos morales.
            </p>
            <p>
              • Las obligaciones de confidencialidad vigentes por 3 años posteriores a la
              terminación del contrato.
            </p>
            <p>• Jurisdicción: Monterrey, Nuevo León.</p>
            <p className="text-neutral-500 pt-2 border-t border-neutral-800">
              El contrato formal será enviado por nuestro equipo legal tras la aceptación
              de esta propuesta.
            </p>
          </div>
        </div>

        {/* Aceptación (solo cuando allowAccept=true, es decir vista pública) */}
        {allowAccept && (
          <div className="mb-16">
            {aceptado ? (
              <div className="p-8 bg-green-500/10 border-2 border-green-500/30 rounded-2xl text-center">
                <CheckCircle2 className="w-14 h-14 text-green-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">Propuesta aceptada</h3>
                <p className="text-neutral-400 mb-4">
                  Gracias por tu confianza. Nuestro equipo se pondrá en contacto para los
                  siguientes pasos.
                </p>
                {quote.aceptacion && (
                  <div className="text-xs text-neutral-500">
                    Aceptada por{' '}
                    <span className="text-white">{quote.aceptacion.nombre}</span> ·{' '}
                    {new Date(quote.aceptacion.fecha).toLocaleString('es-MX')}
                  </div>
                )}
              </div>
            ) : (
              <div className="p-8 bg-gradient-to-br from-brand-gold/10 to-brand-gold/5 border border-brand-gold/30 rounded-2xl">
                <h3 className="text-2xl font-bold text-white mb-2">
                  ¿Listo para arrancar?
                </h3>
                <p className="text-neutral-300 mb-6">
                  Acepta esta propuesta y comencemos a construir juntos.
                </p>
                <button
                  onClick={() => setShowAcceptModal(true)}
                  className="px-6 py-3 rounded-lg font-medium text-base hover:scale-[1.02] transition-transform"
                  style={{ background: BRAND.gold, color: BRAND.black }}
                >
                  Aceptar Propuesta →
                </button>
                <div className="text-xs text-neutral-500 mt-4">
                  ⚠️ TODO Alejandro: la aceptación legal vinculante debe pasar por
                  TRATO+Digid. Hoy solo registra nombre+fecha como aceptación informal.
                </div>
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="pt-12 border-t border-neutral-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <XperienceLogo className="w-10 h-10" />
              <div>
{/*                 <div className="text-white text-xs font-bold tracking-[0.2em]">
                  {submarca.name.toUpperCase()}
                </div> */}
{/*                 <div className="text-[10px] text-neutral-500 tracking-[0.2em]">
                  {submarca.tag.toUpperCase()}
                </div> */}
              </div>
            </div>
            <div className="text-xs text-neutral-500 text-right">
              <div>Calzada Mauricio Fernández Garza 801</div>
              <div>San Pedro Garza García, N.L.</div>
              <div>www.xperience.group · contacto@xperience.group</div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal aceptación */}
      {showAcceptModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur flex items-center justify-center z-50 p-4">
          <div className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 max-w-md w-full">
            <h3 className="text-xl font-bold text-white mb-4">Confirmar aceptación</h3>
            <p className="text-sm text-neutral-400 mb-4">
              Al aceptar esta propuesta queda registrada tu intención de contratar.
              Escribe tu nombre completo para confirmar.
            </p>
            <input
              type="text"
              value={nombreAcepta}
              onChange={(e) => setNombreAcepta(e.target.value)}
              placeholder="Nombre completo"
              className="w-full px-4 py-2.5 bg-black/40 border border-neutral-800 rounded-lg text-white placeholder-neutral-600 focus:outline-none focus:border-brand-gold/50 text-sm mb-5"
            />
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={() => setShowAcceptModal(false)}
                className="px-4 py-2 text-sm text-neutral-400 hover:text-white"
              >
                Cancelar
              </button>
              <button
                onClick={handleAccept}
                disabled={!nombreAcepta.trim()}
                className="px-5 py-2.5 rounded-lg font-medium disabled:opacity-30"
                style={{ background: BRAND.gold, color: BRAND.black }}
              >
                Confirmar aceptación
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
